import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { ArrowBackIcon as ArrowBack } from 'components/common/Icon';
import {
  FormationsTertiaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import { queryClient } from 'states/reactQueryClient';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import {
  BUSINESS_DESCRIPTION_LIMIT,
  companyDetailsBIBCustomerStep1,
  companyDetailsCustomerStep2,
} from 'schemas/companyDetailsSchema';
import {
  FormationsFormActions,
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormData,
  FormationsFormFields,
  FormationsFormItem,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import {
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'services/account';
import { Company } from 'models/company';
import { ENTITY_MAPPING } from 'constants/common';
import {
  useUpdateCompany,
  useIndustryOptions,
  useCurrentAccount,
  useUpdateAccountProgress,
} from 'hooks/api';
import {
  findIndustryTitleFromCode,
} from 'components/IndustryOptions/helpers';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  getProgressTrackerStageStatus,
  isProgressTrackerStageCompleted,
} from 'components/ProgressTracker/Incorporation/state';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';
import { STATES } from '../../../enums';

interface Step1FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const Step1Form = ({ onSubmit, companyData, onBackClick }: Step1FormProps) => {
  const isScorp = companyData.entityType === 'S-CORP';
  const isSoleProp = companyData.entityType === ENTITY_MAPPING.sole_prop;
  const solePropStep1Fields = [
    {
      type: FormationsFormFields.Suggested,
      name: 'suggested',
      placeholder: 'Company Name',
      label: 'Company Name',
      maxLength: 80,
    },
  ];
  const fields: IFormField[] = solePropStep1Fields;

  const formInstance = useForm({
    defaultValues: {
      suggested: companyData?.suggested
        ? companyData.suggested.map((item: any) => item?.name ?? item)
        : ['', '', ''],
    },
    resolver: yupResolver(companyDetailsBIBCustomerStep1),
    context: {
      isSoleProp,
      isScorp,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance;

  return (
    <FormationsFormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Grid container justifyContent="space-between">
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isSubmitting}
            >
              Back
            </FormationsGhostButton>
            <FormationsTertiaryButton
              isLoading={isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-1"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Grid>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface Step2FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const mapState = (state: { name: string; code: string }) => ({
  name: state.name,
  value: state.code,
});
const Step2Form = ({ companyData, onSubmit, onBackClick }: Step2FormProps) => {
  const { data: industryOptionsData } = useIndustryOptions();
  const isScorp = companyData.entityType === 'S-CORP';
  const isSoleProp = companyData.entityType === ENTITY_MAPPING.sole_prop;
  const fields = [
    {
      type: FormationsFormFields.AutoComplete,
      name: 'stateOfIncorporation',
      label: 'State of Incorporation',
      options: {
        placeholder: 'Washington',
        options: STATES.map(mapState),
      },
    },
    {
      type: FormationsFormFields.AutoComplete,
      name: 'industry',
      label: 'Business Industry',
      options: {
        placeholder: '',
        options: industryOptionsData?.map(({ title, code }) => ({
          name: title,
          value: code,
        })),
      },
    },
    {
      type: FormationsFormFields.TextArea,
      name: 'description',
      label: 'Business Description',
      options: {
        placeholder: 'Description for your business',
        characterLimit: BUSINESS_DESCRIPTION_LIMIT,
      },
    },
  ];
  const formInstance = useForm({
    defaultValues: {
      stateOfIncorporation: STATES.find(
        (item) => item.code === companyData.stateOfIncorporation,
      )?.code,
      industry: industryOptionsData?.find(
        (item) => item.code === companyData.industry,
      )?.code,
      description: companyData.description,
    },
    resolver: yupResolver(companyDetailsCustomerStep2),
    context: {
      isSoleProp,
      isScorp,
    },
  });
  
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance;

  return (
    <FormationsFormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <FormationsFormItem key={field.name}>
                  {renderField(field, formInstance)}
                </FormationsFormItem>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isSubmitting}
              data-testid="back-step-2"
            >
              Back
            </FormationsGhostButton>
            <FormationsTertiaryButton
              isLoading={isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-2"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Box>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface TParams {
  handleComplete: (status: ProgressTrackerStatus) => void;
  stageCompleted: boolean;
  companyData: Company;
  status: ProgressTrackerStatus | null;
  onBackClick: () => void;
}

export const shouldShowReadOnly = (
  companyData: Company,
  status: ProgressTrackerStatus | null,
): boolean => {
  if (status === ProgressTrackerStatus.Completed) {
    return true;
  }
  const fields = [
    companyData.name,
    companyData.incorporationDate,
    companyData.stateOfIncorporation,
    companyData.industry,
    companyData.description,
  ];
  const allFilled = fields.every((i) => i != null);
  if (!allFilled) {
    return false;
  }
  if (status === ProgressTrackerStatus.Started && companyData.ein == null) {
    return true;
  }
  return false;
};

export const CompanyDetailsBusinessInABox = ({
  handleComplete,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stageCompleted,
  companyData,
  status,
  onBackClick,
}: TParams) => {
  const [formStep, setFormStep] = useState(0);
  const { t } = useTranslation();
  const { data: industryOptionsData } = useIndustryOptions();

  const { mutateAsync: updateCompany } = useUpdateCompany({
    onSuccess: () =>
      queryClient.invalidateQueries(['getCompany', companyData?.id]),
  });

  const onStep1Submit = async (formData: Company) => {
    const companyDetails = {
      suggestedNames: formData?.suggested?.filter((name) => name),
    };
    if (companyData?.id) {
      await updateCompany({ id: companyData.id, data: companyDetails });
      setFormStep(1);
      if (status === null) {
        handleComplete(ProgressTrackerStatus.Started);
      }
    }
  };

  const onStep2Submit = async (formData: Company) => {
    const companyDetails = {
      industry: formData.industry,
      stateOfIncorporation: formData.stateOfIncorporation,
      description: formData.description,
    };
    if (companyData?.id) {
      await updateCompany({ id: companyData.id, data: companyDetails });
      handleComplete(ProgressTrackerStatus.Completed);
    }
  };

  return (
    <div>
      <Typography variant="h5B" component="h5" data-test-id="company-details">
        Company Details
      </Typography>
      {shouldShowReadOnly(companyData, status) ? (
        <ReadOnlyForm
          items={[
            {
              key: 'company-name',
              title: 'Company Name',
              value:
                companyData?.name ??
                companyData?.suggested?.map(
                  (item: any) => item?.name ?? item,
                ) ??
                '',
            },
            {
              key: 'State-of-Incorporation',
              title: 'State of Incorporation',
              value: companyData?.stateOfIncorporation,
            },
            {
              key: 'Business-Industry',
              title: 'Business Industry',
              value: findIndustryTitleFromCode(
                industryOptionsData ?? [],
                companyData?.industry,
              ),
            },
            {
              key: 'Business-Description',
              title: 'Business Description',
              value: companyData?.description,
            },
          ]}
        />
      ) : (
        <>
          <Box
            sx={{
              display: formStep === 0 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">
              {t('progressTracker.incorporation.businessInABoxStep1Subtitle')}
            </SubtitleText>
            <Step1Form
              onSubmit={onStep1Submit}
              companyData={companyData}
              onBackClick={onBackClick}
            />
          </Box>
          <Box
            sx={{
              display: formStep === 1 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">
              {t('progressTracker.incorporation.whatBusinessIndustryYouAreIn')}
            </SubtitleText>
            <Step2Form
              companyData={companyData}
              onSubmit={onStep2Submit}
              onBackClick={() => {
                setFormStep(0);
              }}
            />
          </Box>
        </>
      )}
    </div>
  );
};

interface ContainerProps {
  company: Company;
  onBackClick: () => void;
}

export const CompanyDetailsContainerBusinessInABox = ({
  company,
  onBackClick,
}: ContainerProps) => {
  const { currentAccount } = useCurrentAccount();
  const { mutateAsync: updateAccountProgress } = useUpdateAccountProgress(
    currentAccount?.id,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['progressTracker']);
      },
    },
  );
  const handleComplete = async (status: ProgressTrackerStatus) => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.Incorporation,
      status,
      stage: ProgressTrackerStages.CompanyDetails,
    };

    await updateAccountProgress({
      progress: [requestProps],
      eventData: {
        stage: ProgressTrackerStages.CompanyDetails,
        stageStatus: status,
        entityType: company?.entityType ?? company?.legacyEntityType ?? '',
      },
    });
  };

  const stateCompleted = isProgressTrackerStageCompleted(
    currentAccount ?? null,
    ProgressTrackerStages.CompanyDetails,
  );
  const status = getProgressTrackerStageStatus(
    currentAccount ?? null,
    ProgressTrackerStages.CompanyDetails,
  );

  return (
    <CompanyDetailsBusinessInABox
      handleComplete={handleComplete}
      status={status ?? null}
      stageCompleted={stateCompleted}
      companyData={company}
      onBackClick={onBackClick}
    />
  );
};
